import { useContext } from 'react'

import { Button } from '@hhgtech/hhg-components/mantine'
import { rem } from '@mantine/core'
import { useAudioPlayer } from 'react-use-audio-player'

import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { actionTypes } from 'state/actionTypes'
import { AppContext } from 'state/appContext'

import {
  StyledAudioPlayerControlsWrapper,
  StyledAudioPlayerIcon,
} from './index.styled'
import { ArticleAudioPlayerState } from './types'

type Props = {
  articleId: number
  audioPlayerState: ArticleAudioPlayerState
  className?: string
  onClickPlay?: () => void
}

export function AudioPlayerControls({
  articleId,
  audioPlayerState,
  className,
  onClickPlay,
}: Props) {
  const { play, pause } = useAudioPlayer()
  const { dispatch } = useContext(AppContext)
  const currentUrl = useCurrentUrl()
  const isMobile = useIsMobile()
  function handleClickPlay() {
    onClickPlay?.()
    play()
    dispatch({ type: actionTypes.PLAY_ARTICLE, payload: { id: articleId } })
  }

  function handleClickPause() {
    pause()
  }

  return (
    <StyledAudioPlayerControlsWrapper className={className}>
      {['idle', 'paused', 'ended'].includes(audioPlayerState) && (
        <Button
          size="md"
          type="button"
          variant="primary"
          id="test-play"
          onClick={handleClickPlay}
          dataEventCategory={'Audio'}
          dataEventAction={'Request Article Audio'}
          dataEventLabel={currentUrl}
          styles={(theme) => ({
            root: {
              padding: 0,
              borderRadius: '50%',
              width: `${rem(32)} !important`,
              height: `${rem(32)} !important`,
              backgroundColor: theme.colors.primaryBase,
              img: {
                width: '20px',
                height: '20px',
              },
              [theme.fn.smallerThan('md')]: {
                backgroundColor: 'initial',
                width: `${rem(24)} !important`,
                height: `${rem(22)} !important`,
              },
            },
          })}
        >
          {isMobile ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M9.75 9.625V14.375C9.75 14.7417 9.90833 15.0127 10.225 15.188C10.5417 15.3627 10.85 15.3417 11.15 15.125L14.875 12.75C15.1583 12.5833 15.3 12.3333 15.3 12C15.3 11.6667 15.1583 11.4167 14.875 11.25L11.15 8.875C10.85 8.65833 10.5417 8.63733 10.225 8.812C9.90833 8.98733 9.75 9.25833 9.75 9.625ZM12 21.5C10.6833 21.5 9.446 21.25 8.288 20.75C7.12933 20.25 6.125 19.575 5.275 18.725C4.425 17.875 3.75 16.8707 3.25 15.712C2.75 14.554 2.5 13.3167 2.5 12C2.5 10.6833 2.75 9.44567 3.25 8.287C3.75 7.129 4.425 6.125 5.275 5.275C6.125 4.425 7.12933 3.75 8.288 3.25C9.446 2.75 10.6833 2.5 12 2.5C13.3167 2.5 14.5543 2.75 15.713 3.25C16.871 3.75 17.875 4.425 18.725 5.275C19.575 6.125 20.25 7.129 20.75 8.287C21.25 9.44567 21.5 10.6833 21.5 12C21.5 13.3167 21.25 14.554 20.75 15.712C20.25 16.8707 19.575 17.875 18.725 18.725C17.875 19.575 16.871 20.25 15.713 20.75C14.5543 21.25 13.3167 21.5 12 21.5Z"
                fill="#2D87F3"
              />
            </svg>
          ) : (
            <StyledAudioPlayerIcon
              width="24"
              height="24"
              src="/svg/player-play.svg"
            />
          )}
        </Button>
      )}
      {audioPlayerState === 'playing' && (
        <Button
          size="md"
          type="button"
          onClick={handleClickPause}
          variant="primary"
          styles={(theme) => ({
            root: {
              padding: '0 !important',
              [theme.fn.largerThan('md')]: {
                borderRadius: '50%',
                width: `${rem(32)} !important`,
                height: `${rem(32)} !important`,
                backgroundColor: theme.colors.primaryBase,
                display: 'flex',
                alignItems: 'center',
                gap: rem(4),
                margin: 0,
                span: {
                  display: 'none',
                },
                '&:before, &:after': {
                  content: '""',
                  height: '15px',
                  width: '4px',
                  borderRadius: '4px',
                  backgroundColor: theme.fn.primaryColor(),
                  marginTop: '1px',
                },
              },
              [theme.fn.smallerThan('md')]: {
                width: `${rem(24)} !important`,
                height: `${rem(24)} !important`,
                backgroundImage: `url('data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M9.4 15.75H10.9V8.25H9.4V15.75ZM13.1 15.75H14.6V8.25H13.1V15.75ZM12 21.5C10.6833 21.5 9.446 21.25 8.288 20.75C7.12933 20.25 6.125 19.575 5.275 18.725C4.425 17.875 3.75 16.8707 3.25 15.712C2.75 14.554 2.5 13.3167 2.5 12C2.5 10.6833 2.75 9.44567 3.25 8.287C3.75 7.129 4.425 6.125 5.275 5.275C6.125 4.425 7.12933 3.75 8.288 3.25C9.446 2.75 10.6833 2.5 12 2.5C13.3167 2.5 14.5543 2.75 15.713 3.25C16.871 3.75 17.875 4.425 18.725 5.275C19.575 6.125 20.25 7.129 20.75 8.287C21.25 9.44567 21.5 10.6833 21.5 12C21.5 13.3167 21.25 14.554 20.75 15.712C20.25 16.8707 19.575 17.875 18.725 18.725C17.875 19.575 16.871 20.25 15.713 20.75C14.5543 21.25 13.3167 21.5 12 21.5Z" fill="%232D87F3"/></svg>')`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundColor: 'transparent',
              },
            },
          })}
        />
      )}
    </StyledAudioPlayerControlsWrapper>
  )
}
